import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { IP_ADDRESS_REGEX } from '@e3dc-react/shell/Libs/validation'
import {
  StringSchema,
  BooleanSchema,
  NumberSchema,
  string,
  boolean,
  number,
  array,
  object,
  mixed,
  ArraySchema,
  ObjectSchema,
  AnyObject,
  MixedSchema,
  ref,
} from 'yup'
import { ValidationMessageKeys } from 'src/Configuration/validation/translation-keys'
import { ChargeStrategy, WallboxPhaseType } from '..'
import { ChargeStrategyType, ChargeStrategyUnit, CombinedChargeStrategyDto, StorageSoftwareVersion } from '../types'

import {
  CombinedChargeStrategyConfigurationForm,
  DailyEnergyConsumptionForm,
} from '../models/wallbox-configuration/wallbox-configuration-settings-form.model'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'
import { isVersionGreaterThanOrEqualTo } from '../utils'
import { numberSchema, stringSchema } from 'src/Configuration/validation'
import { WallboxType } from '../dto'

export const HYSTERESIS_MIN = 1
export const HYSTERESIS_MAX = 1440

export const DEVICE_NAME_MAX_LENGTH = 20
export const DEVICE_NAME_MIN_LENGTH = 1
export const CONSUMPTION_ENERGY_DISABLED = 0
export const OCPP_CHARGE_POINT_OPERATOR_REGEX = /^wss?:\/\//
export const LED_INTENSITY_MIN = 30
export const LED_INTENSITY_MAX = 100

export type ValidationFieldFns = {
  // #region Common
  deviceNameField: () => StringSchema<string>
  ledIntensityField: () => NumberSchema
  autoPhaseSwitchEnabledField: () => BooleanSchema
  cableLockField: () => BooleanSchema
  activeChargeStrategyField: () => MixedSchema<ChargeStrategy | undefined>
  weekdaysField: () => ArraySchema<DailyEnergyConsumptionForm[] | undefined, AnyObject, '', ''>
  chargeStrategyField: () => ObjectSchema<CombinedChargeStrategyDto>
  chargeStrategyConfigurationField: () => ArraySchema<CombinedChargeStrategyConfigurationForm[] | undefined, AnyObject, '', ''>
  chargeLimitTypeField: () => NumberSchema<number | undefined>
  wallboxTypeField: () => NumberSchema<WallboxType>
  // #endregion
  // #region Easy- / Multi-connect
  // #region Default
  sunModeActiveField: () => BooleanSchema<boolean>
  numberPhasesField: (phases: WallboxPhaseType[]) => NumberSchema<WallboxPhaseType>
  maxChargeCurrentField: () => NumberSchema<number>
  upperCurrentLimitField: () => NumberSchema<number>
  minChargeCurrentField: () => NumberSchema<number>
  chargeStopHysteresisField: () => NumberSchema<number>

  // #region Edsn
  minChargePowerField: () => NumberSchema<number>
  maxChargePowerField: () => NumberSchema<number>
  maxDischargePowerField: () => NumberSchema<number>
  lowerChargePowerLimitField: () => NumberSchema<number>
  upperChargePowerLimitField: () => NumberSchema<number>
  lowerDischargePowerLimitField: () => NumberSchema<number>
  upperDischargePowerLimitField: () => NumberSchema<number>
  // #endregion
  dhcpEnabledField: () => BooleanSchema<boolean>
  ipAddressField: () => StringSchema<string>
  subnetMaskField: () => StringSchema<string>
  gatewayField: () => StringSchema<string>
  keyLockModeField: () => StringSchema
  rfidReaderEnabledField: () => BooleanSchema
  authRequiredField: () => BooleanSchema<boolean>
  // #endregion
  // #region Bidi
  diagBatCapacityField: () => NumberSchema<number>
  bidiModeActiveField: () => BooleanSchema<boolean>
  // #endregion
  // #region Ocpp
  ocppEnabledField: () => BooleanSchema<boolean>
  ocppIdField: () => StringSchema
  ocppServerAddressField: () => StringSchema
  ocppAuthTypeField: () => BooleanSchema
  ocppAllowLocalChargeField: () => BooleanSchema
  ocppPasswordField: () => StringSchema
  // #endregion
}

/*
 * a hook that provides validation schemas for wallbox settings form fields
 */
const useWallboxSettingsFormValidationFields = (): ValidationFieldFns => {
  const { t } = useCustomTranslation()
  const numberSchemaWithoutTranslation = numberSchema(t)
  const stringSchemaWithoutTranslation = stringSchema(t)

  const { storage } = useActiveInstallationContext()
  const softwareVersion = storage?.softwareRelease
  const isNewSoftwareVersion = softwareVersion ? isVersionGreaterThanOrEqualTo(softwareVersion, StorageSoftwareVersion.X_2024_04) : false

  const dailyEnergyConsumptionSchema: ObjectSchema<DailyEnergyConsumptionForm> = object({
    daytime: string().required(),
    minEnergy: numberSchemaWithoutTranslation({ min: CONSUMPTION_ENERGY_DISABLED }).required(),
  })

  const validateValueBasedOnUnit = (schema: NumberSchema<number>, wallboxType: WallboxType | undefined): NumberSchema<number> =>
    schema.when('chargeStrategyUnit', ([chargeStrategyUnit], currentSchema) => {
      return Number(chargeStrategyUnit) === 2 || wallboxType === WallboxType.EDSN
        ? currentSchema.integer().max(100, t(ValidationMessageKeys.max, { value: 100 }))
        : currentSchema
    })

  const chargeStrategyConfigurationSchema = (wallboxType?: WallboxType): ObjectSchema<CombinedChargeStrategyConfigurationForm> =>
    object().shape({
      chargeStrategyWeekday: number().required(),
      chargeStrategyUnit: mixed<ChargeStrategyUnit>()
        .transform((value, originalValue) => {
          if (typeof originalValue === 'string' && !isNaN(Number(originalValue))) {
            return Number(originalValue)
          }
          return value
        })
        .oneOf([0, 1, 2])
        .required(),
      chargeStrategyDirectChargeAmount: validateValueBasedOnUnit(number().required(), wallboxType),
      chargeStrategyDelayedChargeAmount: validateValueBasedOnUnit(number().required(), wallboxType),
      chargeStrategyDelayedChargeTime: string().required(),
      chargeStrategyTotalChargeAmount: number().required(),
    })

  const requiredBooleanWhenOcppEnabled = (): BooleanSchema =>
    boolean()
      .optional()
      .when('ocppEnabled', ([ocppEnabled], schema) => (ocppEnabled ? schema.required() : schema))

  return {
    // #region Common
    deviceNameField: () => stringSchemaWithoutTranslation({ minLength: DEVICE_NAME_MIN_LENGTH, maxLength: DEVICE_NAME_MAX_LENGTH }).trim().required(),
    ledIntensityField: () => numberSchemaWithoutTranslation({ min: LED_INTENSITY_MIN, max: LED_INTENSITY_MAX }),
    autoPhaseSwitchEnabledField: () => boolean().optional(),
    cableLockField: () => boolean().optional(),
    activeChargeStrategyField: () =>
      mixed<ChargeStrategy>().when('$isNewSoftwareVersion', (any, schema) => (isNewSoftwareVersion ? schema : schema.required())),
    weekdaysField: () =>
      array()
        .of(dailyEnergyConsumptionSchema)
        .when('$isNewSoftwareVersion', (any, schema) => (isNewSoftwareVersion ? schema : schema.length(7).required())),
    chargeStrategyField: () =>
      object()
        .shape({
          chargeStrategyType: number<ChargeStrategyType>().required(),
        })
        .when('$isNewSoftwareVersion', (any, schema) => (isNewSoftwareVersion ? schema.required() : schema)),
    chargeStrategyConfigurationField: () =>
      array()
        .of(chargeStrategyConfigurationSchema())
        .when('wallboxType', ([wallboxType], schema) => schema.of(chargeStrategyConfigurationSchema(wallboxType)))
        .length(7)
        .when('$isNewSoftwareVersion', (any, schema) => (isNewSoftwareVersion ? schema.required() : schema)),

    chargeLimitTypeField: () => number().optional(),

    wallboxTypeField: () => number<WallboxType>().required(),
    // #endregion
    // #region Easy-/Multi-connect
    // #region Default
    sunModeActiveField: () => boolean().required(),
    numberPhasesField: (phases: WallboxPhaseType[]) => number<WallboxPhaseType>().oneOf(phases).required(),
    maxChargeCurrentField: () => number().required(),
    upperCurrentLimitField: () => number().required(),
    minChargeCurrentField: () =>
      number()
        .required()
        .when('upperCurrentLimit', ([upperCurrentLimit], schema) =>
          schema.max(upperCurrentLimit, t(ValidationMessageKeys.max, { value: upperCurrentLimit }))
        )
        .when('lowerCurrentLimit', ([lowerCurrentLimit], schema) =>
          schema.min(lowerCurrentLimit, t(ValidationMessageKeys.min, { value: lowerCurrentLimit }))
        ),

    /* MinChargePower: [lowerChargePowerLimit,  current maxChargePower]*/
    minChargePowerField: () =>
      number()
        .required()
        .min(ref('lowerChargePowerLimit'), ({ min }) => t(ValidationMessageKeys.min, { value: min }))
        .max(ref('maxChargePower'), ({ max }) => t(ValidationMessageKeys.max, { value: max })),

    /* maxChargePower: [current minChargePower,  upperChargePowerLimit]*/
    maxChargePowerField: () =>
      number()
        .required()
        .min(ref('minChargePower'), ({ min }) => t(ValidationMessageKeys.min, { value: min }))
        .max(ref('upperChargePowerLimit'), ({ max }) => t(ValidationMessageKeys.max, { value: max })),

    /* maxDischargePower: [lowerDischargePowerLimit,  upperDischargePowerLimit]*/
    maxDischargePowerField: () =>
      number()
        .required()
        .min(ref('lowerDischargePowerLimit'), ({ min }) => t(ValidationMessageKeys.min, { value: min }))
        .max(ref('upperDischargePowerLimit'), ({ max }) => t(ValidationMessageKeys.max, { value: max })),

    lowerChargePowerLimitField: () => number().required(),
    upperChargePowerLimitField: () => number().required(),
    lowerDischargePowerLimitField: () => number().required(),
    upperDischargePowerLimitField: () => number().required(),

    chargeStopHysteresisField: () => numberSchemaWithoutTranslation({ min: HYSTERESIS_MIN, max: HYSTERESIS_MAX }).required(),
    // #endregion
    dhcpEnabledField: () => boolean().required(),
    ipAddressField: () => string().trim().matches(IP_ADDRESS_REGEX, t('validators.invalidIpAddress')).required(),
    subnetMaskField: () => string().trim().matches(IP_ADDRESS_REGEX, t('validators.invalidIpAddress')).required(),
    gatewayField: () => string().trim().matches(IP_ADDRESS_REGEX, t('validators.invalidIpAddress')).required(),
    keyLockModeField: () => string().optional(),
    rfidReaderEnabledField: () => boolean().optional(),
    authRequiredField: () => boolean().required(),
    // #endregion
    // #region Bidi
    diagBatCapacityField: () => number().required(),
    bidiModeActiveField: () => boolean().required(),
    // #endregion
    // #region Ocpp
    ocppEnabledField: () => boolean().required(),
    ocppIdField: () =>
      string()
        .optional()
        .when('ocppEnabled', ([ocppEnabled], schema) => (ocppEnabled ? schema.required() : schema)),
    ocppServerAddressField: () =>
      string()
        .optional()
        .when('ocppEnabled', ([ocppEnabled], schema) =>
          ocppEnabled ? schema.trim().matches(OCPP_CHARGE_POINT_OPERATOR_REGEX, t('validators.invalid-ocpp-server-address')).required() : schema
        ),
    ocppAuthTypeField: () => requiredBooleanWhenOcppEnabled(),
    ocppAllowLocalChargeField: () => requiredBooleanWhenOcppEnabled(),
    ocppPasswordField: () => string(),
    // #endregion
  }
}

export default useWallboxSettingsFormValidationFields
